/**
 * Edge-compatible code application API
 *
 * This module works on:
 * - Node.js
 * - Cloudflare Workers
 * - Vercel Edge Functions
 * - Deno Deploy
 * - Browser environments
 *
 * For file-based operations, use executeEditFile from core.ts
 */

import { createTwoFilesPatch } from 'diff';
import OpenAI from 'openai';
import { SDK_VERSION } from '../../version.js';
import { logger } from '../../logger.js';
import {
  createAnchorCompactionPlan,
  DEFAULT_ANCHOR_COMPACTION_THRESHOLD,
  FastApplyAnchorIntegrityError,
} from './anchors.js';
import type {
  EditChanges,
  ApplyEditInput,
  ApplyEditResult,
  ApplyEditConfig,
} from './types.js';

const DEFAULT_API_URL = 'https://api.morphllm.com';
const DEFAULT_TIMEOUT = 600000;

type CompletionChunk = {
  id?: string;
  choices?: Array<{
    delta?: { content?: string | null };
    finish_reason?: string | null;
  }>;
};

type CompletionResponse = {
  id?: string;
  choices?: Array<{
    message?: { content?: string | null };
    finish_reason?: string | null;
  }>;
};

export class FastApplyContextLengthError extends Error {
  readonly code = 'context_length_exceeded';
  readonly completionId?: string;

  constructor(completionId?: string) {
    super(
      'Fast Apply exhausted its context window before producing the complete file. ' +
      'Retry with a smaller input/update or use sparse anchors for unchanged code.'
    );
    this.name = 'FastApplyContextLengthError';
    this.completionId = completionId;
  }
}

export { FastApplyAnchorIntegrityError } from './anchors.js';

function isAsyncIterable(value: unknown): value is AsyncIterable<CompletionChunk> {
  return typeof value === 'object' && value !== null && Symbol.asyncIterator in value;
}

async function collectCompletion(
  response: unknown
): Promise<{ content: string; completionId?: string; finishReason?: string | null }> {
  if (!isAsyncIterable(response)) {
    const completion = response as CompletionResponse;
    return {
      content: completion.choices?.[0]?.message?.content ?? '',
      completionId: completion.id,
      finishReason: completion.choices?.[0]?.finish_reason,
    };
  }

  const content: string[] = [];
  let completionId: string | undefined;
  let finishReason: string | null | undefined;

  for await (const chunk of response) {
    completionId = chunk.id ?? completionId;
    for (const choice of chunk.choices ?? []) {
      if (choice.delta?.content) {
        content.push(choice.delta.content);
      }
      if (choice.finish_reason !== undefined && choice.finish_reason !== null) {
        finishReason = choice.finish_reason;
      }
    }
  }

  return { content: content.join(''), completionId, finishReason };
}

/**
 * Generate a unified diff between two strings
 */
export function generateUdiff(
  original: string,
  modified: string,
  filepath: string
): string {
  return createTwoFilesPatch(
    filepath,
    filepath,
    original,
    modified,
    'Original',
    'Modified'
  );
}

/**
 * Count changes from a unified diff
 */
export function countChanges(original: string, modified: string): EditChanges {
  const diff = generateUdiff(original, modified, 'file');
  const lines = diff.split('\n');

  let linesAdded = 0;
  let linesRemoved = 0;

  for (const line of lines) {
    if (line.startsWith('+') && !line.startsWith('+++')) {
      linesAdded++;
    } else if (line.startsWith('-') && !line.startsWith('---')) {
      linesRemoved++;
    }
  }

  const linesModified = Math.min(linesAdded, linesRemoved);

  return {
    linesAdded: linesAdded - linesModified,
    linesRemoved: linesRemoved - linesModified,
    linesModified,
  };
}

/**
 * Call Morph Apply API to merge code edits
 * Uses OpenAI SDK for reliable connection handling, retries, and timeouts
 */
export async function callMorphAPI(
  originalCode: string,
  codeEdit: string,
  instructions: string,
  filepath: string,
  config: ApplyEditConfig
): Promise<{ content: string; completionId?: string }> {
  const apiKey = config.morphApiKey || (typeof process !== 'undefined' ? process.env?.MORPH_API_KEY : undefined);
  const apiUrl = config.morphApiUrl || DEFAULT_API_URL;
  const useLarge = config.large ?? (typeof process !== 'undefined' ? process.env?.MORPH_LARGE_APPLY !== 'false' : true);
  const model = useLarge ? 'morph-v3-large' : 'morph-v3-fast';
  const timeout = config.timeout ?? DEFAULT_TIMEOUT;
  const debug = config.debug || false;

  if (!apiKey) {
    throw new Error(
      'Morph API key not found. Set MORPH_API_KEY environment variable or pass morphApiKey in config.'
    );
  }

  const anchorThreshold = config.anchorCompactionThreshold
    ?? DEFAULT_ANCHOR_COMPACTION_THRESHOLD;
  const anchorPlan = config.anchorCompaction !== false
    && originalCode.length >= anchorThreshold
    ? createAnchorCompactionPlan(originalCode, [codeEdit, instructions])
    : undefined;
  const requestCode = anchorPlan?.code ?? originalCode;
  const requestInstructions = anchorPlan
    ? instructions + anchorPlan.instructionSuffix
    : instructions;

  // Format message with XML tags as per Morph Fast Apply spec
  const message = `<instruction>${requestInstructions}</instruction>\n<code>${requestCode}</code>\n<update>${codeEdit}</update>`;

  logger.debug('FastApply', 'http_request', {
    url: `${apiUrl}/v1/chat/completions`,
    model,
    filepath,
    instruction_len: instructions.length,
    original_len: originalCode.length,
    request_code_len: requestCode.length,
    code_edit_len: codeEdit.length,
    anchor_count: anchorPlan?.anchorCount ?? 0,
    unique_anchor_count: anchorPlan?.uniqueAnchorCount ?? 0,
  });

  const startTime = Date.now();

  const client = new OpenAI({
    apiKey,
    baseURL: `${apiUrl}/v1`,
    timeout,
    maxRetries: config.retryConfig?.maxRetries ?? 3,
    defaultHeaders: {
      'X-Morph-SDK-Version': SDK_VERSION,
      ...(anchorPlan ? { 'X-Morph-Apply-Context': 'extralong' } : {}),
    },
  });

  try {
    const maxAttempts = anchorPlan?.hasStructuralValidation ? 2 : 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const response = await client.chat.completions.create({
        model,
        messages: [{ role: 'user', content: message }],
        stream: true,
      });
      const completion = await collectCompletion(response);

      if (completion.finishReason === 'length') {
        throw new FastApplyContextLengthError(completion.completionId);
      }

      let content = completion.content;
      if (!content) {
        throw new Error('Morph API returned empty response');
      }

      if (anchorPlan) {
        try {
          content = anchorPlan.expand(content);
        } catch (error) {
          if (error instanceof FastApplyAnchorIntegrityError) {
            if (attempt < maxAttempts) {
              logger.warn('FastApply', 'anchor_validation_retry', {
                attempt,
                completion_id: completion.completionId,
              });
              continue;
            }
            throw new FastApplyAnchorIntegrityError(completion.completionId);
          }
          throw error;
        }
      }

      const elapsed = Date.now() - startTime;
      logger.debug('FastApply', 'http_response', { status: 200, completion_id: completion.completionId, content_len: content.length, latency_ms: elapsed });

      return { content, completionId: completion.completionId };
    }

    throw new FastApplyAnchorIntegrityError();
  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    const status = error?.status || error?.response?.status;
    logger.error('FastApply', 'http_error', {
      status,
      error: error?.message,
      latency_ms: elapsed,
    });

    if (status === 401) {
      const err = new Error(
        'Authentication failed: Your Morph API key is invalid or has been revoked. ' +
        'Please visit https://morphllm.com to get a valid API key, then update your MCP configuration.'
      );
      (err as any).status = 401;
      throw err;
    }

    if (status === 429) {
      const err = new Error(
        'Rate limited: You\'ve exceeded your Morph API usage limits. ' +
        'Please visit https://morphllm.com to check your plan and purchase additional credits.'
      );
      (err as any).status = 429;
      throw err;
    }

    throw error;
  }
}

/**
 * Apply an edit to code directly without file I/O
 *
 * This is the edge-compatible code-in/code-out API that accepts code content directly
 * and returns the merged result without reading or writing any files.
 *
 * Works on Cloudflare Workers, Vercel Edge Functions, Deno, and browsers.
 *
 * @param input - Code and edit parameters
 * @param config - Optional configuration
 * @returns Result with merged code
 *
 * @example
 * ```typescript
 * import { applyEdit } from '@morphllm/morphsdk';
 *
 * const result = await applyEdit({
 *   originalCode: fs.readFileSync('file.ts', 'utf-8'),
 *   codeEdit: '// ... existing code ...\nconst newVar = 42;\n// ... existing code ...',
 *   instructions: 'Add a new variable',
 *   // filepath is accepted but does nothing
 * });
 *
 * if (result.success) {
 *   fs.writeFileSync('file.ts', result.mergedCode);
 * }
 * ```
 */
export async function applyEdit(
  input: ApplyEditInput,
  config: ApplyEditConfig = {}
): Promise<ApplyEditResult> {
  const filepath = input.filepath || 'file';

  try {
    logger.debug('FastApply', 'apply_edit_start', { original_len: input.originalCode.length, code_edit_len: input.codeEdit.length });

    const instruction = input.instruction ?? input.instructions ?? '';
    const { content: mergedCode, completionId } = await callMorphAPI(
      input.originalCode,
      input.codeEdit,
      instruction,
      filepath,
      config
    );

    const udiff = config.generateUdiff !== false
      ? generateUdiff(input.originalCode, mergedCode, filepath)
      : undefined;

    const changes = countChanges(input.originalCode, mergedCode);

    return {
      success: true,
      mergedCode,
      udiff,
      changes,
      completionId,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    logger.error('FastApply', 'apply_edit_error', { error: errorMessage });
    const structuredError = error instanceof FastApplyContextLengthError
      || error instanceof FastApplyAnchorIntegrityError
      ? error
      : undefined;

    return {
      success: false,
      changes: { linesAdded: 0, linesRemoved: 0, linesModified: 0 },
      error: errorMessage,
      errorCode: structuredError?.code,
      completionId: structuredError?.completionId,
    };
  }
}
